import { useEffect, useState } from "react";
import "./Streak.css";
import { getYearlyCoupon } from "../../services/master.service";
import copyIcon from "../../static/images/copy.png";
import Q from "../../static/images/q_streak.png";

export default function Streaks() {
  const NUM_MONTHS = 11;
  const userId = localStorage.getItem("user_token");

  const [showCelebration, setShowCelebration] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [couponCode, setCouponCode] = useState(null);

  const [currentMonthTotal, setCurrentMonthTotal] = useState(0);
  const [balanceToTarget, setBalanceToTarget] = useState(0);
  const [cutoffAmount, setCutoffAmount] = useState(0);

  const remainingMonths = Math.max(NUM_MONTHS - completedCount,0);
  const progressPercent = Math.min(Math.round(
    ((completedCount / NUM_MONTHS) * 100)),100);

  const [customers, setCustomers] = useState(() => {
    const saved = localStorage.getItem("customers");
    const parsed = saved ? JSON.parse(saved) : {};

    if (userId && !parsed[userId]) {
      parsed[userId] = {
        streak: Array.from({ length: NUM_MONTHS }).map((_, index) => ({
          value: 0,
          status: null,
          monthIndex: index,
          lastUpdatedTime: 0,
        })),
        lastManualUpdateIndex: -1,
        lastPurchaseDate: null,
      };
    }
    return parsed;
  });

  useEffect(() => {
    localStorage.setItem("customers", JSON.stringify(customers));
  }, [customers]);

  const currentCustomer = userId ? customers[userId] : null;

  useEffect(() => {
    if (!userId) return;

    const fetchStreak = async () => {
      try {
        const res = await getYearlyCoupon();
        const data = res?.data?.data;

        const monthCount = data?.month_count || 0;
        setCompletedCount(monthCount);
        setCouponCode(data?.coupon_code || null);

        setCurrentMonthTotal(data?.current_month_total || 0);
        setBalanceToTarget(data?.balance_to_target || 0);
        setCutoffAmount(data?.cutoff_amount || 0);

        setCustomers((prev) => {
          const customer = prev[userId];
          const updatedStreak = customer.streak.map((m, index) => ({
            ...m,
            status: index < monthCount,
          }));

          return {
            ...prev,
            [userId]: { ...customer, streak: updatedStreak },
          };
        });

      } catch (error) {
        console.error("Yearly coupon fetch failed", error);
      }
    };

    fetchStreak();
  }, [userId]);

  const arrowIndex =
    currentCustomer?.streak.length > 0
      ? (() => {
        const reversedIndex = [...currentCustomer.streak]
          .reverse()
          .findIndex((s) => s.status === true);

        return reversedIndex !== -1
          ? currentCustomer.streak.length - 1 - reversedIndex
          : -1;
      })()
      : -1;

  const isFullStreak =
    currentCustomer?.streak.every((item) => item.status === true) ?? false;

  useEffect(() => {
    let timer;
    if (isFullStreak) {
      timer = setTimeout(() => setShowCelebration(true), 2000);
    } else {
      setShowCelebration(false);
    }
    return () => clearTimeout(timer);
  }, [isFullStreak]);

  const getProgressClass = (percent) => {
    if (percent <= 10) return "progress-low";
    if (percent <= 50) return "progress-medium";
    if (percent <= 99) return "progress-good";
    if (percent = 100) return "progress-great";
    return "progress-excellent";
  };

  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const handleCopyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setShowCopyAlert(true);

      setTimeout(() => {
        setShowCopyAlert(false);
      }, 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const [confettiKey, setConfettiKey] = useState(0);

  useEffect(() => {
    if (!showCelebration) return;

    const interval = setInterval(() => {
      setConfettiKey(prev => prev + 1);
    }, 600);

    const stopTimer = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(stopTimer);
    };
  }, [showCelebration]);

  return (
    <div className="container">
      {showCopyAlert && (
        <div className="copy-alert">
          Coupon code copied!
        </div>
      )}

      {!userId ? (
        <h3>Please login to view your streak</h3>
      ) : (
        <div className="streak-layout">
          <div className="streak-card">
            <div className="streak-stats">
              <div className="stat-box" data-type="current">
                <span className="stat-label">Current Month Total</span>
                <span className="stat-value">&#8377; {currentMonthTotal}</span>
              </div>

              <div className="stat-box" data-type="balance">
                <span className="stat-label">Balance to Target</span>
                <span className="stat-value">&#8377; {balanceToTarget}</span>
              </div>

              <div className="stat-box" data-type="cutoff">
                <span className="stat-label">Cutoff Amount</span>
                <span className="stat-value">&#8377; {cutoffAmount}</span>
              </div>
            </div>

            <div className="streak-main">
              <h3 className="streak-title">
                <span className="streak-q">
                  <img src={Q} alt="Q" />
                </span>
                <span className="streak-number">{NUM_MONTHS} Months Streak</span>
              </h3>

              {isFullStreak && couponCode && (
                <div className="coupon-section">
                  <div className="coupon-title">Coupon Unlocked</div>
                  <div className="coupon-box">
                    <span className="coupon-code">{couponCode}</span>
                    <img
                      src={copyIcon}
                      alt="Copy"
                      className="copy-icon"
                      onClick={handleCopyCoupon}
                    />
                  </div>
                  <div className="coupon-hint">Click to copy coupon</div>
                </div>
              )}

              <div className="months-row">
                {Array.from({ length: NUM_MONTHS }).map((_, index) => {
                  const isDone = index < completedCount;
                  const isCurrent = index === completedCount && completedCount < NUM_MONTHS;

                  return (
                    <div
                      key={index}
                      className={`month-circle ${isDone ? "done" : ""} ${isCurrent ? "current" : ""}`}
                    >
                      {isDone ? "\u2713" : isCurrent ? "\u{1F525}" : index + 1}
                    </div>
                  );
                })}
              </div>

              <div className="streak-footer">
                <div className="streak-pill">Remaining: {remainingMonths}</div>
                <div className="streak-pill">{progressPercent}% Completed</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
